import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  selectActivityLogsEndLogId,
  selectActivityLogsStartLogId,
  setActivityLogsStartEndLogIds,
} from './redux';
import {
  buildLogIdDateMsMap,
  clearActivityLogsStartEndLogIdBound,
  nextActivityLogsStartEndLogIds,
  startEndLogIdsFromClicked,
} from './activityLogIdRange';

/** /logs row-lock start/end — slices by activity_logs id; does not turn Time on. */
export const useActivityLogsLogIdRange = (
  logs: Array<{ id?: string | null, date?: string | null } | null | undefined> | null,
) => {
  const dispatch = useAppDispatch();
  const startLogId = useAppSelector(selectActivityLogsStartLogId);
  const endLogId = useAppSelector(selectActivityLogsEndLogId);
  const dateById = useMemo(() => buildLogIdDateMsMap(logs || []), [logs]);

  const applyLogIds = (next: {
    startLogId?: string | null,
    endLogId?: string | null,
  } | null) => {
    dispatch(setActivityLogsStartEndLogIds(
      next
        ? {
          startLogId: next.startLogId || null,
          endLogId: next.endLogId || null,
        }
        : null,
    ));
  };

  const applyLogIdRangeBound = (which: 'start' | 'end', sourceIds: string[]) => {
    const bounds = startEndLogIdsFromClicked(sourceIds, dateById);
    if (!bounds.startLogId) return;
    const next = nextActivityLogsStartEndLogIds(
      which,
      {
        startLogId: bounds.startLogId,
        endLogId: bounds.endLogId || bounds.startLogId,
      },
      { startLogId, endLogId },
      dateById,
    );
    applyLogIds({
      startLogId: next.startLogId || null,
      endLogId: next.endLogId || null,
    });
  };

  const clearLogIdRangeBound = (which: 'start' | 'end') => {
    applyLogIds(clearActivityLogsStartEndLogIdBound(which, { startLogId, endLogId }));
  };

  const clearLogIdRange = () => {
    dispatch(setActivityLogsStartEndLogIds(null));
  };

  return {
    startLogId,
    endLogId,
    dateById,
    applyLogIdRangeBound,
    clearLogIdRangeBound,
    clearLogIdRange,
  };
};
